/**
 * appVersion.ts —— 构建版本自检 + 「陈旧 PWA 缓存」自愈（第 18 轮 BUG-R18-01 伴生修复）。
 *
 * 背景（本轮线上故障根因）：
 *   站点是 PWA（Workbox 预缓存 app-shell）。发过一版「多人对战 UI 已上线、但房间服务地址未注入」
 *   的包；打开过那一版的浏览器会把它精确缓存下来，之后即使线上已修好，用户首屏仍可能运行**旧包**，
 *   旧包只会连同源 `wss://<站点>/api/ws`，被 SPA fallback 返回 200 → 稳定报「连接服务器失败」。
 *
 * 自愈思路（不依赖构建期版本号，纯运行时比对，对历史旧包同样有效）：
 *   ① 取「当前页面实际加载的入口 JS 指纹」（document 里的 module script）；
 *   ② 用 no-store 拉一份服务端最新 index.html，取其入口 JS 指纹；
 *   ③ 两者不一致 = 正在运行陈旧缓存 → 注销 Service Worker + 清空 CacheStorage + 重载一次
 *      （sessionStorage 打标，最多自动重载一次，杜绝刷新死循环）。
 */

const RELOAD_FLAG = 'riverside.staleHealReload';
const ENTRY_RE = /assets\/index-[A-Za-z0-9_-]+\.js/;

export type StaleCheck = 'fresh' | 'stale' | 'unknown';
export type HealResult = 'skipped' | 'fresh' | 'healing' | 'stale-blocked' | 'unknown';

/** 当前页面实际加载的入口 JS 指纹（形如 `assets/index-abc123.js`）。 */
export function loadedEntry(): string | null {
  if (typeof document === 'undefined') return null;
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
  for (const s of scripts) {
    const m = ENTRY_RE.exec(s.getAttribute('src') ?? '');
    if (m) return m[0];
  }
  return null;
}

/** 服务端当前发布的入口 JS 指纹（绕开 SW / HTTP 缓存）。 */
export async function fetchServerEntry(): Promise<string | null> {
  if (typeof fetch !== 'function') return null;
  try {
    const res = await fetch(`/index.html?fresh=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = ENTRY_RE.exec(html);
    return m ? m[0] : null;
  } catch {
    return null;
  }
}

/** 判定当前运行的是否为陈旧缓存包。 */
export async function checkStaleBuild(): Promise<StaleCheck> {
  const local = loadedEntry();
  const remote = await fetchServerEntry();
  if (!local || !remote) return 'unknown';
  return local === remote ? 'fresh' : 'stale';
}

/**
 * 强制刷新：注销所有 Service Worker + 清空 CacheStorage，然后硬重载。
 * 供自愈流程与设置页/错误条的「强制刷新」按钮共用（用户可手动自救）。
 */
export async function hardRefresh(): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch { /* noop */ }
  try {
    const cs = (globalThis as unknown as { caches?: CacheStorage }).caches;
    if (cs?.keys) {
      const keys = await cs.keys();
      await Promise.all(keys.map((k) => cs.delete(k).catch(() => false)));
    }
  } catch { /* noop */ }
  try { location.reload(); } catch { /* noop */ }
}

/**
 * 启动自检：发现陈旧缓存则自动清缓存并重载一次；已重载过仍陈旧则返回 stale-blocked
 * （由 UI 提示用户手动「强制刷新」，避免无限循环）。开发环境直接跳过。
 */
export async function autoHealStaleBuild(): Promise<HealResult> {
  const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
  if (env?.DEV) return 'skipped';
  const verdict = await checkStaleBuild();
  if (verdict !== 'stale') return verdict === 'fresh' ? 'fresh' : 'unknown';
  let already = false;
  try { already = sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { /* noop */ }
  if (already) return 'stale-blocked';
  try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* noop */ }
  void hardRefresh();
  return 'healing';
}
